import { cn } from "@/lib/utils";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useAuth, useI18N, usePostVenueReview } from "common/hooks";
import { useEffect, useRef, useState } from "react";
import { readFile } from "@/helpers/cropImage";
import { XIcon } from "lucide-react";
import Zoom from "react-medium-image-zoom";
import { AddPhotoImageData } from "common/types";
import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import AnalyticsService from "@/services/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import Checkbox from "@/components/common/Checkbox";

type AddReviewModalProps = {
  venueId: string;
  venueName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: any;
};

export function AddReviewModal({
  venueId,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: AddReviewModalProps) {
  const i18n = useI18N();

  const showToast = useCustomToast();

  const venueSuitsAnswer = ["Yes", "No", "Not applicable"];
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const isDark = useDarkMode();
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errors, setErrors] = useState({
    note: false,
    images: attachedImages.map(() => ({ note: false, selectedOption: false })),
  });
  const { mutate, data, error, status } = usePostVenueReview(true);
  const topRef = useRef(null);
  const noteRef = useRef(null);
  const imageRefs = useRef([]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("postReviewErrorSubmittingReview");
      AnalyticsService.trackMx("postReviewErrorSubmittingReview", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      console.log("usePostVenueReview", data);
      showToast(i18n.get("reviewSavedSuccessfully"), "success");
      setAttachedImages([]);
      setNote("");
      setSelectedAnswer("");
      setErrors({
        note: false,
        images: [],
      });
      onClose();
    }
  }, [data, isDark]);

  const validateFields = () => {
    const newErrors = {
      note: note.length < 5,
      images: attachedImages.map((image) => ({
        note: image.note.length < 5,
        selectedOption: !image.selectedOption,
      })),
    };

    setErrors(newErrors);

    const isNoteValid = !newErrors.note;
    const areImagesValid = newErrors.images.every(
      (img) => !img.note && !img.selectedOption
    );

    if (!isNoteValid) {
      noteRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      const firstInvalidImageIndex = newErrors.images.findIndex(
        (img) => img.note || img.selectedOption
      );
      if (firstInvalidImageIndex !== -1) {
        imageRefs.current[firstInvalidImageIndex].scrollIntoView({
          behavior: "smooth",
        });
      }
    }

    return isNoteValid && areImagesValid;
  };

  const submit = async () => {
    if (!validateFields()) {
      return;
    }
    if (venueId && user) {
      if (topRef?.current) {
        topRef.current.scrollIntoView({ behavior: "smooth" });
      }

      AnalyticsService.trackGAEventHandler("postReviewSubmittedReview");

      AnalyticsService.trackMx("postReviewSubmittedReview", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
        TypeOfPhoto: selectedAnswer,
        NoOfPhotos: attachedImages.length,
      });

      mutate({
        images: attachedImages,
        venueId: venueId,
        review: note,
        selectedAnswer,
      });
    } else {
      showToast(i18n.get("mustBeLoggedInToSubmitReview"), "error");
    }
  };

  const onFileChange = async (e) => {
    AnalyticsService.trackGAEventHandler("postReviewUploadedPhoto");
    AnalyticsService.trackMx("postReviewUploadedPhoto", {});

    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
    setErrors((prev) => ({
      ...prev,
      images: [
        ...prev.images,
        ...imgs.map(() => ({ note: false, selectedOption: false })),
      ],
    }));
  };

  const handleOptionPress = (answer: string) => {
    setSelectedAnswer(selectedAnswer === answer ? undefined : answer);
  };

  	const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
        closeButton: "hidden",
      }}
    >
      <section
        ref={topRef}
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-200px)] overflow-y-auto"
        role="none"
      >
        {status === "pending" && <Loader />}

        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            {i18n.get("leaveReview")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div
                  className="self-stretch !text-primary-500 dark:!text-primary-300 paragraph-xsmall-semibold"
                  tabIndex={0}
                >
                  {venueName}
                </div>
                <h1
                  className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                  tabIndex={0}
                >
                  {i18n.get("leaveReview")}
                </h1>
              </div>
              <div
                className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold pt-1"
                tabIndex={0}
              >
                {i18n.get("yourFeedbackHelpCommunity")}
              </div>

              <div className="border w-full border-primary-150 dark:border-neutral-400 mt-2 h-[1px]"></div>
            </div>
            <div className="self-stretch   rounded-lg justify-start items-center gap-8 inline-flex">
              <div className="mb-2">
                <h2
                  className="mobile-h3-small-semi-bold text-primary-900 dark:text-primary-100"
                  tabIndex={0}
                >
                  {i18n.get("wasThisVenueAccessibleForYou")}
                </h2>
                <span
                  className="paragraph-small-semibold !text-primary-500 dark:!text-primary-300 block"
                  tabIndex={0}
                >
                  {i18n.get("chooseOne")}
                </span>

                <div className="flex flex-col">
                  {venueSuitsAnswer.map((answer, index) => (
                    <div key={index} className="my-1" role="none">
                      <Checkbox
                        checked={selectedAnswer === answer}
                        key={index}
                        onCheck={(value) => {
                          if (value) {
                            handleOptionPress(answer);
                          } else {
                            handleOptionPress(undefined);
                          }
                        }}
                        label={answer}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="md:hidden border w-full border-primary-150 dark:border-neutral-400 mt-1 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full ">
                <Textarea
                  id="review"
                  placeholder="Describe your experience"
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={note}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="button-small-medium text-neutral-500 mt-3"
                  tabIndex={0}
                >
                  <span className={note.length < 5 ? "text-red-600" : ""}>
                    {note.length}
                  </span>
                  /500 Characters - at least 5 characters required
                </div>
                {errors.note && (
                  <div className="text-red-600" tabIndex={0}>
                    {i18n.get("describeYourVisitToThisVenue")}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2" tabIndex={0}>
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt="attached image"
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <button
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index
                            )
                          );
                        }}
                        className="absolute top-2 right-3 m-2"
                        aria-label={`Remove attached image ${index + 1}`}
                      >
                        <div className="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex">
                          <div className="w-4 h-4 relative flex-col justify-start items-start flex">
                            <img
                              src="/images/Close.svg"
                              alt="Close"
                              className="w-4 h-4"
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                      </button>
                    </div>

                    <div>
                      <p
                        className="mobile-h2-medium-semi-bold text-primary-800 dark:text-primary-150"
                        tabIndex={0}
                      >
                        {i18n.get("whatIsThisPhotoOf")}
                      </p>
                      <div className="space-y-2 mb-2">
                        {[
                          "Entrance",
                          "Inside Area",
                          "Outdoor Area",
                          "Bathroom",
                        ].map((place, placeIndex) => (
                          <Button
                            key={placeIndex}
                            type="button"
                            onClick={() => {
                              setAttachedImages((prev) => {
                                const newData = [...prev];
                                newData[index].selectedOption = place;
                                return newData;
                              });
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                newErrors.images[index].selectedOption = false;
                                return newErrors;
                              });
                            }}
                            className={cn(
                              "bg-white rounded-full mx-2 text-primary-900 paragraph-small-medium ",
                              place === image.selectedOption
                                ? "bg-[#FFBE74] border border-[#FFBE74]"
                                : "#EEEDFB border border-primary-300 bg-primary-100"
                            )}
                          >
                            {place}
                          </Button>
                        ))}
                      </div>
                      {errors.images[index].selectedOption && (
                        <div className="text-red-500" tabIndex={0}>
                          {i18n.get("pleaseSelectOneOfTheTags")}
                        </div>
                      )}
                    </div>
                    <div>
                      <Textarea
                        id="note"
                        placeholder={i18n.get(
                          "describeThisPhotoForOurBlindUsers"
                        )}
                        className="scrollbar-thin dark:scrollbar-thin-dark h-32"
                        value={image.note}
                        onChange={(e) => {
                          setAttachedImages((prev) => {
                            const newData = [...prev];
                            newData[index].note = e.target.value;
                            return newData;
                          });
                          setErrors((prev) => {
                            const newErrors = { ...prev };
                            newErrors.images[index].note =
                              e.target.value.length < 5;
                            return newErrors;
                          });
                        }}
                      />
                    </div>
                    <div
                      className="button-small-medium text-neutral-500"
                      tabIndex={0}
                    >
                      {image.note.length}/500 characters
                    </div>
                    {errors.images[index].note && (
                      <div className="text-red-500" tabIndex={0}>
                        {i18n.get("describeWhatThisImageShows")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-[327px] justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture" // Wrap the entire div with the label
                className="grow shrink basis-0 h-12 px-6 py-3  bg-primary-50 dark:bg-dark-800 rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
                tabIndex={0}
                role="button"
              >
                <div className="text-center text-primary-800 dark:text-primary-150 mobile-h3-small-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit review"
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
        <button
          onClick={() => {
            setAttachedImages([]);
            setNote("");
            onClose();
          }}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </Modal>
  );
}
